// services/AuthService.js - ReactJS (web)
import api from '../api/api';

const TOKEN_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
};

class AuthService {
  // ==================== TOKEN MANAGEMENT ====================

  /**
   * Store authentication tokens
   */
  async storeTokens(accessToken, refreshToken) {
    try {
      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
      return true;
    } catch (error) {
      console.error('Error storing tokens:', error);
      return false;
    }
  }

  /**
   * Get access token
   */
  async getAccessToken() {
    try {
      return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }

  /**
   * Get refresh token
   */
  async getRefreshToken() {
    try {
      return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
    } catch (error) {
      console.error('Error getting refresh token:', error);
      return null;
    }
  }

  /**
   * Store user data
   */
  async storeUserData(userData) {
    try {
      localStorage.setItem(TOKEN_KEYS.USER_DATA, JSON.stringify(userData));
      return true;
    } catch (error) {
      console.error('Error storing user data:', error);
      return false;
    }
  }

  /**
   * Get user data
   */
  async getUserData() {
    try {
      const data = localStorage.getItem(TOKEN_KEYS.USER_DATA);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  }

  /**
   * Clear all tokens and user data (logout)
   */
  async clearTokens() {
    try {
      localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(TOKEN_KEYS.USER_DATA);
      return true;
    } catch (error) {
      console.error('Error clearing tokens:', error);
      return false;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated() {
    try {
      const token = await this.getAccessToken();
      return !!token;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get auth headers for API requests
   */
  async getAuthHeaders() {
    try {
      const token = await this.getAccessToken();
      if (token) {
        return {
          Authorization: `Bearer ${token}`,
        };
      }
      return {};
    } catch (error) {
      return {};
    }
  }

  // ==================== PUBLIC ROUTES ====================
  // No authentication required

  /**
   * Register new user
   * POST /api/auth/register
   */
  async register(userData) {
    try {
      const response = await api.post('/auth/register', userData);
      
      // Store tokens if returned
      if (response.data?.data?.tokens) {
        const { accessToken, refreshToken } = response.data.data.tokens;
        await this.storeTokens(accessToken, refreshToken);
      }
      
      // Store user data if returned
      if (response.data?.data?.user) {
        await this.storeUserData(response.data.data.user);
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Login user
   * POST /api/auth/login
   */
  async login(email, password) {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      // Store tokens
      if (response.data?.data?.tokens) {
        const { accessToken, refreshToken } = response.data.data.tokens;
        await this.storeTokens(accessToken, refreshToken);
      }
      
      // Store user data
      if (response.data?.data?.user) {
        await this.storeUserData(response.data.data.user);
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Refresh access token
   * POST /api/auth/refresh-token
   */
  async refreshToken() {
    try {
      const refreshToken = await this.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await api.post('/auth/refresh-token', { refreshToken });
      
      // Update tokens
      if (response.data?.data) {
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        await this.storeTokens(accessToken, newRefreshToken);
      }
      
      return response.data;
    } catch (error) {
      // If refresh fails, clear tokens
      await this.clearTokens();
      throw this.handleError(error);
    }
  }

  /**
   * Forgot password - request reset link
   * POST /api/auth/forgot-password
   */
  async forgotPassword(email, language = 'ht') {
    try {
      const response = await api.post('/auth/forgot-password', { email, language });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reset password with token
   * POST /api/auth/reset-password
   */
  async resetPassword(token, newPassword, language = 'ht') {
    try {
      const response = await api.post('/auth/reset-password', { token, newPassword, language });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Verify email with token
   * GET /api/auth/verify-email/:token
   */
  async verifyEmail(token) {
    try {
      const response = await api.get(`/auth/verify-email/${token}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Check if email exists
   * GET /api/auth/check-email
   */
  async checkEmail(email) {
    try {
      const response = await api.get('/auth/check-email', { params: { email } });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Check if phone exists
   * GET /api/auth/check-phone
   */
  async checkPhone(phone) {
    try {
      const response = await api.get('/auth/check-phone', { params: { phone } });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== PROTECTED ROUTES ====================
  // Routes that require authentication

  /**
   * Logout user
   * POST /api/auth/logout
   */
  async logout() {
    try {
      // Attempt to call logout endpoint
      try {
        await api.post('/auth/logout');
      } catch (error) {
        // Ignore errors from logout endpoint
        console.warn('Logout endpoint error:', error.message);
      }
      
      // Clear all tokens and user data
      await this.clearTokens();
      
      return {
        success: true,
        message: 'Logged out successfully'
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Change password
   * POST /api/auth/change-password
   */
async changePassword(currentPassword, newPassword, language) {
  try {
    const payload = {
      currentPassword,
      newPassword
    };
    
    // Only add language if provided
    if (language) {
      payload.language = language;
    }
    
    const response = await api.post('/auth/change-password', payload);
    return response.data;
  } catch (error) {
    throw this.handleError(error);
  }
}

  /**
   * Resend verification email
   * POST /api/auth/resend-verification
   */
  async resendVerification(language = 'ht') {
    try {
      const response = await api.post('/auth/resend-verification', { language });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update user language preference
   * PATCH /api/auth/language
   */
  async updateLanguage(language) {
    try {
      const response = await api.patch('/auth/language', { language });
      
      // Update stored user data
      if (response.data?.data) {
        const userData = await this.getUserData();
        if (userData) {
          userData.language = language;
          await this.storeUserData(userData);
        }
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get user language preference
   * GET /api/auth/language
   */
  async getUserLanguage() {
    try {
      const response = await api.get('/auth/language');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    let errorMessage = ' ';
    
    if (error.response?.data?.data?.error?.message) {
      errorMessage = error.response.data.data.error.message;
    } else if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status,
      data: error.response?.data
    };
  }

  /**
   * Get supported languages
   */
  getSupportedLanguages() {
    return ['en', 'ht', 'fr', 'es'];
  }

  /**
   * Get language name by code
   */
  getLanguageName(code) {
    const languages = {
      en: 'English',
      ht: 'Kreyòl Ayisyen',
      fr: 'Français',
      es: 'Español'
    };
    return languages[code] || code;
  }

  /**
   * Get all languages with names
   */
  getAllLanguages() {
    return this.getSupportedLanguages().map(code => ({
      code,
      name: this.getLanguageName(code)
    }));
  }
}

export default new AuthService();